import { money, pct } from './format.js';

// What the same asset class costs elsewhere in their own portfolio.
//
// The yardstick is never a fund we picked. It is the cheapest fund the
// household already owns in the same broad class, which makes the finding
// impossible to argue with on sourcing and much harder to dismiss: they are
// already buying the cheap version of the thing they are overpaying for.
//
// It does NOT say "switch". Two funds in one asset class are not
// interchangeable, and a growth fund measured against a blend index fund is a
// price comparison, not a recommendation. The copy is careful about that,
// because a client who acts on an overstated claim is a worse outcome than one
// who never reads the section.

export default function FeeComparison({ comparison }) {
  if (!comparison?.rows?.length) return null;

  const { rows, annualSaving, periodSaving, years } = comparison;
  const anyDifferent = rows.some(r => r.differentAssetClass);

  return (
    <div className="asmt-subsection">
      <h3 className="asmt-subhead">You are paying very different prices for the same kind of investment</h3>
      <p className="asmt-note">
        Within each asset class below, one of these funds is the cheapest you already
        own. Levelling the rest to that price would keep{' '}
        <strong>{money(annualSaving)} a year</strong>, or {money(periodSaving)} over {years} years
        at today&rsquo;s balances.
      </p>

      <table className="asmt-table">
        <thead>
          <tr>
            <th>What you hold</th>
            <th className="num">Cost</th>
            <th>Against the cheapest you own</th>
            <th className="num">Cost</th>
            <th className="num">Difference a year</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.ticker}>
              <td style={i === rows.length - 1 ? { borderBottom: 'none' } : undefined}>
                <strong>{r.ticker}</strong>
                <span className="asmt-muted"> · {r.assetClass}</span>
              </td>
              <td className="num">{pct(r.expenseRatioPct, 2)}</td>
              <td>
                <strong>{r.benchmarkTicker}</strong>
                <span className="asmt-muted"> · {r.benchmarkAssetClass}</span>
              </td>
              <td className="num">{pct(r.benchmarkExpenseRatioPct, 2)}</td>
              <td className="num">{money(r.annualSaving)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <p className="asmt-note" style={{ marginTop: 14 }}>
        {anyDifferent && (
          <>
            These are not identical funds, and the more expensive ones are actively
            managed rather than tracking an index. The question is not whether they
            cost more, it is whether they have earned it after that cost.{' '}
          </>
        )}
        The {years}-year figure assumes your balances stay where they are and no growth
        at all. Fees are charged on the balance, so a growing account pays more than
        this, not less.
      </p>
    </div>
  );
}
